import {PromptPool, TTSVoiceType, VisPromptWeightGroup, VisualizationPromptPoolType} from "../../../../../Server/DB/engineConfigs/@EngineConfig/@EC_Visualization.js";
import {EffectPointer} from "../../../../../Server/DB/@Shared/EffectPointer.js";
import {EffectPointerInput} from "../../../../@Shared/EffectPointerInput.js";
import {InAndroid} from "../../../../../Utils/Bridge/Bridge_Native.js";
import {DialogStyle} from "../../../../../Utils/UI/GlobalStyles.js";
import {Clone, GetEntries} from "js-vextensions";
import {observer_mgl} from "mobx-graphlink";
import React, {useState} from "react";
import {Button, CheckBox, Column, Row, RowLR, Select, Spinner, Text, TextArea, TextInput} from "react-vcomponents";
import {ShowMessageBox} from "react-vmessagebox";
import {ES, RunInAction_Set, TextPlus} from "web-vcore";
import {GetDistancesFromRoomToAllOthers, ParseCustomPromptPool} from "../../../../../Engine/LiveSession/Components/Visualization/VisualizationUtils.js";
import {FilterWordList_ForVisualization, GetWordList_MobX} from "../../../../../Engine/LiveSession/Components/Visualization/WordLists.js";
import {GetGridScenesData} from "../../../../../Engine/LiveSession/Components/Visualization/GridScenes.js";
import {store} from "../../../../../Store/index.js";
import {SpinnerForPercent} from "../../../../../Utils/ReactComponents/SpinnerForPercent.js";
import {SharedProps_VisualizationConfigUI} from "../Visualization_ConfigUI.js";
import {SpatialAudioBakeUI} from "./SpatialAudioBakeUI.js";
import {TTSBakingStatus_Visualization} from "./TTSBakingUI.js";
import {ElevenLabsVoiceSelector} from "./VoiceSelector.js";

export const PromptPoolsUI_splitAt = 230;
const splitAt = PromptPoolsUI_splitAt;

export const PromptPoolsUI = observer_mgl((props: SharedProps_VisualizationConfigUI)=>{
	const {enabled, config, ChangeConfig} = props;
	const pools = config.promptPools;
	const [selectedPoolIndex, setSelectedPoolIndex] = useState(0);

	return (
		<>
			<Row mt={5} style={{flexWrap: "wrap"}}>
				<Text>Pools:</Text>
				{pools.map((pool, index)=>{
					return <Button key={index} ml={5} p="2px 5px" text={`${index + 1}: ${pool.name}${!pool.enabled ? " [d]" : ""}`}
						style={ES(
							selectedPoolIndex == index && {backgroundColor: "rgba(100,200,100,.5)"},
						)}
						onClick={()=>setSelectedPoolIndex(index)}/>;
				})}
				<Button ml={5} mdIcon="plus" enabled={enabled} onClick={()=>{
					ChangeConfig(c=>c.promptPools.push(new PromptPool()));
				}}/>
			</Row>
			{pools.length > selectedPoolIndex &&
			<PromptPoolUI {...props} pool={pools[selectedPoolIndex]} poolIndex={selectedPoolIndex}/>}
		</>
	);
});

export const PromptPoolUI = observer_mgl((props: {pool: PromptPool, poolIndex: number} & SharedProps_VisualizationConfigUI)=>{
	const {pool, poolIndex, enabled, config, ChangeConfig} = props;
	const uiState = store.main.engine.config;

	const ChangePool = (poolChangerFunc: (pool: PromptPool)=>void)=>{
		ChangeConfig(c=>{
			const newPool = Clone(pool) as PromptPool;
			poolChangerFunc(newPool);
			c.promptPools[poolIndex] = newPool;
		});
	};

	return (
		<Column ml={10}>
			<Row mt={5}>
				<Text>Name:</Text>
				<TextInput ml={5} enabled={enabled} value={pool.name} onChange={val=>ChangePool(a=>a.name = val)}/>
				<CheckBox ml={5} text="Enabled" enabled={enabled} value={pool.enabled} onChange={val=>ChangePool(a=>a.enabled = val)}/>
				<Button ml={5} mdIcon="content-copy" enabled={enabled} onClick={()=>{
					RunInAction_Set(()=>uiState.viz_copiedPromptPool = Clone(pool));
				}}/>
				<Button ml={5} mdIcon="content-paste" enabled={enabled} onClick={()=>{
					ShowMessageBox({
						title: "Replace prompt-pool?", cancelButton: true,
						message: ()=><Column>
							<Row>Permanently replace this prompt-pool with the copied one?</Row>
							<Row>{`Copied prompt-pool: "${uiState.viz_copiedPromptPool?.name}"`}</Row>
						</Column>,
						onOK: ()=>{
							ChangeConfig(c=>{
								c.promptPools[poolIndex] = Clone(uiState.viz_copiedPromptPool);
							});
						},
					});
				}}/>
				<Button ml={5} mdIcon="arrow-left" title="Move left" enabled={enabled && poolIndex > 0} onClick={()=>{
					ChangeConfig(c=>{
						const newPools = c.promptPools;
						newPools.Move(newPools[poolIndex], poolIndex - 1);
					});
				}}/>
				<Button ml={5} mdIcon="arrow-right" title="Move right" enabled={enabled && poolIndex < config.promptPools.length - 1} onClick={()=>{
					ChangeConfig(c=>{
						const newPools = c.promptPools;
						newPools.Move(newPools[poolIndex], poolIndex + 1);
					});
				}}/>
				<Button ml={5} mdIcon="delete" enabled={enabled} onClick={()=>{
					ShowMessageBox({
						title: "Delete pool?", cancelButton: true,
						message: `Permanently delete this pool?`,
						onOK: ()=>{
							ChangeConfig(c=>c.promptPools.RemoveAt(poolIndex));
						},
					});
				}}/>
			</Row>
			<RowLR mt={5} splitAt={splitAt}>
				<TextPlus info={`
					The pool's weight determines its likelihood of being selected, among the set of enabled pools.
					* Note: Weight is used for "fresh pool selections"; but pools can get "locked in" as active for >1 prompt. (see "Lock in..." setting below)
				`.AsMultiline(0)}>Weight:</TextPlus>
				<Spinner enabled={enabled} min={0} step={1} value={pool.weight} onChange={val=>ChangePool(a=>a.weight = val)}/>
			</RowLR>
			<RowLR mt={5} splitAt={splitAt}>
				<TextPlus info={`
					When this pool is selected, ensure it gets between X and Y prompts selected for it in a row.
					(1 = equivalent to "no lock-in")
				`.AsMultiline(0)}>Lock in:</TextPlus>
				<Text>Min:</Text>
				<Spinner ml={5} enabled={enabled} min={1} step={1} value={pool.lockIn_min} onChange={val=>ChangePool(a=>a.lockIn_min = val)}/>
				<Text ml={5}>Max:</Text>
				<Spinner ml={5} enabled={enabled} min={1} step={1} value={pool.lockIn_max} onChange={val=>ChangePool(a=>a.lockIn_max = val)}/>
				<CheckBox ml={5} text="Force switch after" enabled={enabled} value={pool.lockIn_forceSwitchAfter} onChange={val=>ChangePool(a=>a.lockIn_forceSwitchAfter = val)}/>
			</RowLR>
			<RowLR mt={5} splitAt={splitAt}>
				<TextPlus info={`
					If set, this changes this pool's prompt-interval (ie. delay after its prompt plays) away from the global default.
					The interval adjustment is still applied to the final value.
					(0 = disable override)
				`.AsMultiline(0)}>Prompt interval override:</TextPlus>
				<Spinner enabled={enabled} min={0} step="any" value={pool.promptInterval_override ?? 0} onChange={val=>ChangePool(a=>a.promptInterval_override = val == 0 ? null : val)}/>
				<Text>s</Text>
				{pool.promptInterval_override != null && <Button ml={5} text="Clear" onClick={()=>ChangePool(a=>a.promptInterval_override = null)}/>}
			</RowLR>
			<RowLR mt={5} splitAt={splitAt}>
				<Text>Prompt volume:</Text>
				<SpinnerForPercent min={0} value={pool.promptVolume} onChange={val=>ChangePool(a=>a.promptVolume = val)}/>
			</RowLR>
			<RowLR mt={5} splitAt={splitAt}>
				<TextPlus info={`
					Interval between subprompt repetitions of the last spoken prompt. (in seconds)
					Subprompts start at the time the main prompt fires, and repeat at this interval for the remainder of the prompt period.
					Example: prompt interval 5s, subprompt interval 1s -> sequence: [prompt, sub, sub, sub, sub].
					(0 = disable subprompts)
				`.AsMultiline(0)}>Subprompt interval:</TextPlus>
				<Spinner enabled={enabled} min={0} step={.1} value={pool.subpromptInterval ?? 0} onChange={val=>ChangePool(a=>a.subpromptInterval = val == 0 ? null : val)}/>
				<Text>s</Text>
			</RowLR>
			<RowLR mt={5} splitAt={splitAt}>
				<TextPlus info={`
					Comma-separated volumes (0–100+, interpreted as percents) for each subprompt tick.
					If the list is shorter than the tick count, the last value is reused.
					If the list is empty, uses the regular prompt-volume.
				`.AsMultiline(0)}>Subprompt volumes:</TextPlus>
				<TextInput style={{flex: 1}} value={(pool.subpromptVolumes ?? []).map(v=>(v * 100).RoundTo_Str(.01)).join(", ")}
					onChange={val=>{
						const parsed = val.split(",").map(s=>parseFloat(s.trim()) / 100).filter(n=>!isNaN(n));
						ChangePool(a=>a.subpromptVolumes = parsed.length ? parsed : []);
					}}/>
			</RowLR>
			<RowLR mt={5} splitAt={splitAt}>
				<TextPlus info="Prefix text to add before the TTS prompt.">TTS prefix:</TextPlus>
				<TextInput enabled={enabled} value={pool.ttsPrefix} onChange={val=>ChangePool(a=>a.ttsPrefix = val)}/>
			</RowLR>
			<RowLR mt={5} splitAt={splitAt}>
				<TextPlus sel info={`
					Text appended to the TTS of the LAST subprompt able to be heard (before the next main prompt plays).
					Variables:
					* $sceneLetter: The target scene's letter. (a single letter, uppercase; only filled in for grid-scene pools)
					* $sceneEntityName: The target scene's anchor-entity name. (only filled in for grid-scene pools)
				`.AsMultiline(0)}>TTS suffix (for last subprompt):</TextPlus>
				<TextInput enabled={enabled} value={pool.ttsSuffix_forLastSubprompt} onChange={val=>ChangePool(a=>a.ttsSuffix_forLastSubprompt = val)}/>
			</RowLR>
			<RowLR mt={5} splitAt={splitAt}>
				<TextPlus info="The source target-pool for visualization prompts.">Prompt target-pool type:</TextPlus>
				<Select options={GetEntries(VisualizationPromptPoolType, "ui")} value={pool.type} onChange={val=>ChangePool(a=>a.type = val)}/>
			</RowLR>
			{pool.type == VisualizationPromptPoolType.custom && <>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info="The TTS engine to use for visualization prompts.">TTS voice type:</TextPlus>
					<Select options={GetEntries(TTSVoiceType, "ui")} value={pool.ttsVoiceType} onChange={val=>ChangePool(a=>a.ttsVoiceType = val)}/>
				</RowLR>
				{pool.ttsVoiceType == TTSVoiceType.elevenLabs && <>
					<ElevenLabsVoiceSelector pool={pool} enabled={enabled} ChangePool={ChangePool}/>
				</>}
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						Convert digits (0-9) to word form (Zero-Nine) for better TTS pronunciation.
						Example: '1KitchenSink' becomes 'OneKitchenSink'.
						Note: Digits are converted "standalone", eg. "12Punch" becomes "OneTwoPunch".
					`.AsMultiline(0)}>Convert digits to words:</TextPlus>
					<CheckBox enabled={enabled} value={pool.ttsConvertDigitsToWords ?? false} onChange={val=>ChangePool(a=>a.ttsConvertDigitsToWords = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						Max distance to next target, when selecting from custom word-pool rooms/targets.
						Note: If a room is a direct-neighbor (linked) to the current room, its targets are always considered in-range.
					`.AsMultiline(0)}>Max. distance to next target:</TextPlus>
					<Spinner step={1} min={0} value={pool.distToNextTarget_max} onChange={val=>ChangePool(a=>a.distToNextTarget_max = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						Min distance to next target, when selecting from custom word-pool rooms/targets.
						Note: If applying this filter would exclude all candidates for next-target, its application is canceled.
					`.AsMultiline(0)}>Min. distance to next target:</TextPlus>
					<Spinner step={1} min={0} value={pool.distToNextTarget_min} onChange={val=>ChangePool(a=>a.distToNextTarget_min = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						Sets whether selection-weights will be equalized among rooms, or individual targets.
						Note: Individual targets has the drawback that selection can get "stuck" in rooms with many targets.
					`.AsMultiline(0)}>Equalize selection weight among:</TextPlus>
					<Select options={GetEntries(VisPromptWeightGroup, "ui")} value={pool.equalizeSelectionWeightAmong} onChange={val=>ChangePool(a=>a.equalizeSelectionWeightAmong = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						Number of prompts over which to block repeats of the same target.
						Note: If applying this filter would exclude all candidates for next-target, its application is canceled.
					`.AsMultiline(0)}>Block target repeats for X prompts:</TextPlus>
					<Spinner step={1} min={0} value={pool.blockTargetRepeatsForXPrompts} onChange={val=>ChangePool(a=>a.blockTargetRepeatsForXPrompts = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						Number of prompts over which to block repeats of the same room.
						Note: If applying this filter would exclude all candidates for next-target, its application is canceled.
					`.AsMultiline(0)}>Block room repeats for X prompts:</TextPlus>
					<Spinner step={1} min={0} value={pool.blockRoomRepeatsForXPrompts} onChange={val=>ChangePool(a=>a.blockRoomRepeatsForXPrompts = val)}/>
				</RowLR>
				<Row mt={5}>
					<Text>Graph validity: {GetGraphValidityStr(pool)}</Text>
				</Row>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						The custom list of words to use for visualization prompts.
						Format: One room per line (optional name before colon); targets in the same room separated by commas.
						Example: "Kitchen: Sink, Fridge, Oven"
						Note: If you don't care about rooms/distances, just put all targets in one line/unnamed-room, eg. "Sink, Fridge, Oven".
					`.AsMultiline(0)}>Custom target pool:</TextPlus>
					<TextArea enabled={enabled} autoSize={true} value={pool.customPoolStr} onChange={val=>ChangePool(a=>a.customPoolStr = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<Text>TTS baking:</Text>
					<TTSBakingStatus_Visualization pool={pool} enabled={enabled}/>
				</RowLR>
			</>}
			{pool.type == VisualizationPromptPoolType.customSounds && <>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info="The audio effect to play continuously in the background for this pool. Use soundFile type; the spatial_* fields within it configure the 3D audio behavior.">Sound effect:</TextPlus>
					<EffectPointerInput enabled={enabled ?? false} value={pool.cs_effect ?? new EffectPointer()} onChange={val=>ChangePool(a=>a.cs_effect = val)}/>
				</RowLR>
				{InAndroid(1) && <RowLR mt={5} splitAt={splitAt}>
					<Text>Bake audio:</Text>
					<Text>Duration:</Text>
					<Spinner ml={5} min={0} step={1} value={store.main.engine.config.spatialAudioBake_duration}
						onChange={val=>RunInAction_Set(()=>store.main.engine.config.spatialAudioBake_duration = val)}/>
					<Text>s</Text>
					<TextPlus ml={5} info="0 = no override">Volume override:</TextPlus>
					<SpinnerForPercent ml={5} mr={5} min={0} value={store.main.engine.config.spatialAudioBake_volumeOverride} onChange={val=>RunInAction_Set(()=>store.main.engine.config.spatialAudioBake_volumeOverride = val)}/>
					<SpatialAudioBakeUI pool={pool} bakeDuration={store.main.engine.config.spatialAudioBake_duration} volumeOverride={store.main.engine.config.spatialAudioBake_volumeOverride}/>
				</RowLR>}
			</>}
			{pool.type == VisualizationPromptPoolType.brysbaert && <>
				<Row mt={5}>
					<Text>Prompt count given these filters: {FilterWordList_ForVisualization(GetWordList_MobX() ?? [], pool, true).length}</Text>
				</Row>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						Minimum "concreteness" rating (1-5) of a word, for inclusion in visualization prompts.
					`.AsMultiline(0)}>Min. word concreteness:</TextPlus>
					<Spinner step={.1} value={pool.concreteness_min} onChange={val=>ChangePool(a=>a.concreteness_min = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						Minimum percent of people who know the word, for inclusion in visualization prompts.
					`.AsMultiline(0)}>Min. percent known:</TextPlus>
					<SpinnerForPercent value={pool.percentKnown_min} onChange={val=>ChangePool(a=>a.percentKnown_min = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						Words with any of these tags will be excluded from the prompt pool. (comma-separated)
						Note: Standard tags (at time of writing): BadResolve, BadCrawl, NSFW
					`.AsMultiline(0)}>Exclusion tags:</TextPlus>
					<TextInput enabled={enabled} style={{flex: 1}}
						value={(pool.exclusionTags ?? []).join(", ")} onChange={val=>ChangePool(a=>a.exclusionTags = val.split(",").map(b=>b.trim()).filter(b=>b != ""))}/>
					<Button ml={5} text="All" onClick={()=>ChangePool(a=>a.exclusionTags = ["BadResolve", "BadCrawl", "NSFW"])}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						Whether to "merge" words that are duplicates (after ignoring spaces and hyphens) in the prompt pool.
						Note: For the concreteness and percent-known requirements, at least 1 variant must match both requirements.
						Note: For the exclusion-tags requirements, all variants must match, ie. none can have a tag match. (atm; may change later)
					`.AsMultiline(0)}>Merge duplicates:</TextPlus>
					<CheckBox enabled={enabled} value={pool.mergeDuplicates ?? false} onChange={val=>ChangePool(a=>a.mergeDuplicates = val)}/>
					<Button ml={5} text="Find duplicates" onClick={()=>{
						const filtered_noMerging = FilterWordList_ForVisualization(GetWordList_MobX() ?? [], {...pool, mergeDuplicates: false}, true);
						const filtered_withMerging = new Set(FilterWordList_ForVisualization(GetWordList_MobX() ?? [], {...pool, mergeDuplicates: true}, true));
						const variantsLostFromMerging = filtered_noMerging.filter(a=>!filtered_withMerging.has(a))
							//.toSorted(Intl.Collator("en").compare); // this works, but I dislike how it's a bit less "precisely predictable"
							.OrderBy(a=>a.toLowerCase());
						ShowMessageBox({
							title: "Duplicate words info",
							message: ()=><Column style={DialogStyle({width: 800, height: 500})}>
								<Row>Prompt count is {filtered_noMerging.length} (no merging) / {filtered_withMerging.size} (with merging).</Row>
								<Text>Variants lost from merging: ({variantsLostFromMerging.length})</Text>
								<TextArea mt={5} style={{flex: 1}} value={variantsLostFromMerging.join(", ")}/>
							</Column>
						});
					}}/>
				</RowLR>
			</>}
			{pool.type == VisualizationPromptPoolType.gridScenes && <>
				<Row mt={5}>
					<Text>Prompt count given these filters: {GetGridScenesData().filter(a=>a.generation.Distance(pool.targetGridGen ?? 0) <= pool.maxGridGenDistance).length}</Text>
				</Row>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						Which generation the pool is centered on, relative to the latest scene for each A-Z grid-cell.
						(0 = the most recent scene for each grid-cell; 2 = the scene two generations older than the most recent)
					`.AsMultiline(0)}>Target generation:</TextPlus>
					<Spinner enabled={enabled} min={0} step={1} value={pool.targetGridGen} onChange={val=>ChangePool(a=>a.targetGridGen = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						How many "generations away" from the target generation a scene can be (in either direction) for it to be included in the active pool.
						(0 = only scenes at exactly the target generation)
					`.AsMultiline(0)}>Max. gen-distance:</TextPlus>
					<Spinner enabled={enabled} min={0} step={1} value={pool.maxGridGenDistance} onChange={val=>ChangePool(a=>a.maxGridGenDistance = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						If enabled, don't select a scene of the same letter/grid-cell twice until every cell has had a selection. (ie. "round-robin" selection across cells)
						(note: the "repeat" avoidance is by cell, NOT scene; eg. if CellA->Scene1 is selected, CellA->Scene2 is blocked till a scene is selected for cells B-Z)
					`.AsMultiline(0)}>Avoid repeats till all cells used:</TextPlus>
					<CheckBox enabled={enabled} value={pool.avoidRepeatsTillAllCellsUsed ?? false} onChange={val=>ChangePool(a=>a.avoidRepeatsTillAllCellsUsed = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						After this many prompts (within this pool), the shared target-generation-adjustment is increased by 1.
						Eg. 26 = one bump per full A-Z cell sweep; 52 = two prompts per cell before the bump.
						Note: The new adjustment-value is voiced after the last prompt's TTS finishes. (-1/null = disabled)
					`.AsMultiline(0)}>Bump target-gen after X prompts:</TextPlus>
					<Spinner enabled={enabled} min={-1} value={pool.bumpTargetGenAfterXPrompts} onChange={val=>ChangePool(a=>a.bumpTargetGenAfterXPrompts = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						After the phase has been in phase "dreaming" for this many straight minutes, the shared target-generation
						live-adjustment is reset back to 0. (-1/null = disabled; 0 = reset immediately on entering dreaming phase)
					`.AsMultiline(0)}>Reset target-gen after X mins dreaming:</TextPlus>
					<Spinner enabled={enabled} min={-1} value={pool.resetTargetGenAfterXMinsOfDreaming} onChange={val=>ChangePool(a=>a.resetTargetGenAfterXMinsOfDreaming = val)}/>
				</RowLR>
				<Row mt={5}>
					<TextPlus info={`
						Scenes whose generation-offset from the target-generation equals X (offset 0 = the target generation itself; negative = newer than target, positive = older than target) get their pick-weight multiplied by Y.
						Note: If a scene's offset matches no entry: 100%.
					`.AsMultiline(0)}>Scene pick-multipliers (by generation-offset):</TextPlus>
					<Button ml={5} mdIcon="plus" enabled={enabled} onClick={()=>{
						ChangePool(a=>{
							if (!a.recencyBoosts) a.recencyBoosts = {};
							// find the first open slot, fill it with 1
							for (let i = 0; i < 100; i++) {
								if (a.recencyBoosts[i] == null) {
									a.recencyBoosts[i] = 1;
									break;
								}
							}
						});
					}}/>
				</Row>
				{Object.entries(pool.recencyBoosts ?? {}).map(([genOffsetStr, boostAmount], index)=>{
					const genOffset = parseInt(genOffsetStr);
					return (
						<Row key={index} ml={10} mt={5}>
							<Text>Gen-offset:</Text>
							<Spinner ml={5} enabled={enabled} min={-100} step={1} value={genOffset} onChange={newGenOffsetForEntry=>ChangePool(a=>{
								if (!a.recencyBoosts) a.recencyBoosts = {};
								delete a.recencyBoosts[genOffset];
								a.recencyBoosts[newGenOffsetForEntry] = boostAmount;
							})}/>
							<Text ml={5}>Multiplier:</Text>
							<SpinnerForPercent ml={5} enabled={enabled} step={100} max={Infinity} value={boostAmount} onChange={val=>ChangePool(a=>{
								if (!a.recencyBoosts) a.recencyBoosts = {};
								a.recencyBoosts[genOffset] = val;
							})}/>
							<Button ml={5} mdIcon="delete" enabled={enabled} onClick={()=>{
								ChangePool(a=>{
									if (!a.recencyBoosts) return;
									delete a.recencyBoosts[genOffset];
								});
							}}/>
						</Row>
					);
				})}
			</>}
		</Column>
	);
});

export function GetGraphValidityStr(pool: {customPoolStr?: string}) {
	try {
		const rooms = ParseCustomPromptPool(pool.customPoolStr);
		if (rooms.length == 0) return "No rooms defined";

		// pick the first room as starting point, and get all reachable rooms from there
		const startRoom = rooms[0].name;
		const reachableRooms = GetDistancesFromRoomToAllOthers(rooms, startRoom);

		// check if all rooms are reachable
		const unreachableRooms = rooms.filter(room=>!reachableRooms.has(room.name));
		if (unreachableRooms.length > 0) {
			return `Invalid: ${unreachableRooms.length} unreachable rooms: ${unreachableRooms.map(r=>r.name).join(", ")}`;
		}

		return `Valid (${rooms.length} rooms, with ${rooms.flatMap(r=>r.targets_full).length} targets, all connected)`;
	} catch (error) {
		return `Parse error: ${error?.["message"] ?? error}`;
	}
}